import { cn } from 'design-system'
import { X, Info } from 'lucide-react'
import * as R from 'ramda'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import * as bannerActions from 'src/store/action-methods/banner'

import useKeyHandlers from 'src/hooks/useKeyHandlers'
import useTimer from 'src/hooks/useTimer'

import Button from 'src/components/generics/button'
import ProgressBar from 'src/components/generics/progress-bar'

import * as constants from './constants'

const timeToDismiss = 5000

const selector = R.prop('banner')

const Banner = () => {
  const dispatch = useDispatch()

  const {
    open = false,
    msg = null,
    assertive = false,
    type = constants.types.INFO
  } = useSelector(selector)

  const ref = useRef()
  const [height, setHeight] = useState(0)

  const { value, init, reset, resume, stop, pause, paused } = useTimer()

  const close = () => {
    dispatch(bannerActions.setBanner({ open: false }))
    stop()
  }

  useKeyHandlers(
    {
      ESC: () => {
        if (open) {
          close()
        }
      },
      SPACE: () => {
        if (open && !paused) {
          pause()
        }
      }
    },
    [open, paused]
  )

  useEffect(() => {
    if (open) {
      init()
      if (ref.current) {
        setHeight(ref.current.clientHeight)
      }
    } else {
      stop()
      setHeight(0)
    }

    return () => {
      stop()
    }
  }, [open])

  useEffect(() => {
    if (open && msg) {
      reset()
    }
  }, [open, msg])

  useEffect(() => {
    if (value >= timeToDismiss) {
      close()
    }
  }, [value])

  const bannerType = type || constants.types.INFO

  return (
    <>
      <div style={{ height }} />
      <div
        ref={ref}
        className={cn(`yd-banner yd-banner--${bannerType}`, 'fixed w-full', {
          hidden: !open
        })}
        style={{ top: 'var(--contract-banner-height, 0px)' }}
        onMouseOver={pause}
        onMouseOut={resume}>
        <div className="mx-auto flex max-w-screen-lg items-center p-3">
          <div className="w-full">
            <p className="mb-2 flex items-center gap-x-2 text-sm uppercase text-white">
              <Info size={16} className="shrink-0" />
              {bannerType}
            </p>
            <div
              role="status"
              className="text-base font-normal text-white"
              aria-live={assertive ? 'assertive' : 'polite'}>
              {msg}
            </div>
          </div>
          <Button
            circle
            color="white"
            className="ml-2 !flex shrink-0 items-center justify-center text-base"
            aria-label="dismiss banner"
            onClick={close}>
            <X size={20} aria-hidden />
          </Button>
        </div>
        <div className="absolute bottom-0 w-full">
          <ProgressBar
            percent={(value * 100) / timeToDismiss}
            theme={bannerType}
          />
        </div>
      </div>
    </>
  )
}

export { constants }

export default Banner
